import { useState, useEffect } from 'react';
import { Document, DocumentStatus } from '../../types/document.ts';
import { ArrowLeft, Download, Edit, Clock, User, Tag, FileText, GitBranch, CheckCircle, XCircle, AlertCircle, ThumbsUp, ThumbsDown, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { commitDocument, getDocumentById, updateDocument } from '../../services/documentService.ts';

// interface DocumentDetailProps {
//   documentId: string;
//   onBack: () => void;
//   onEdit: (id: string) => void;
// }

export default function DocumentManageDetail() {
   const { id } = useParams(); // 来自 /documentsList/:id/show
  const navigate = useNavigate();
  const notify = useNotify();

  const [document, setDocument] = useState<Document | undefined>();
  const [loading, setLoading] = useState(!!id);
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [commitAction, setCommitAction] = useState<'Approve' | 'Reject'>('Approve');
  const [commitComments, setCommitComments] = useState('');
  const [committing, setCommitting] = useState(false);
  const [showObsoleteModal, setShowObsoleteModal] = useState(false);
  const [markingObsolete, setMarkingObsolete] = useState(false);

  useEffect(() => {
    if (id) {
      const loadDocument = async () => {
        try {
          const doc = await getDocumentById(id);
          setDocument(doc as any);
          setLoading(false);
        } catch (error: any) {
          console.error('Failed to load document:', error);
          notify(`Failed to load document: ${error.message}`, { type: 'error' });
          setLoading(false);
        }
      };
      loadDocument();
    }
  }, [id, notify]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading document...</div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Document not found</div>
      </div>
    );
  }

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Review':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'Obsolete':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleDownload = (fileUrl?: string, version?: string) => {
    if (fileUrl) {
      alert(`Download file: ${document.documentNumber} v${version}\nFile: ${fileUrl}`);
    }
  };

  const handleCommit = async () => {
    if (!id) return;
    setCommitting(true);
    try {
      await commitDocument(id, commitAction, commitComments);
      notify(`Document ${commitAction === 'Approve' ? 'approved' : 'rejected'} successfully!`, { type: 'success' });
      setShowCommitModal(false);
      setCommitComments('');
      // Optionally refresh or redirect
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      notify(error.message || 'Failed to commit document', { type: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const handleMarkObsolete = async () => {
    if (!id || !document) return;
    
    setMarkingObsolete(true);
    try {
      // Update document status to Obsolete
      const updateData = {
        title: document.title,
        description: document.description,
        category: document.category,
        status: 'Obsolete',
        tags: document.tags,
      };
      
      await updateDocument(id, updateData as any);
      notify('Document marked as Obsolete successfully!', { type: 'success' });
      setShowObsoleteModal(false);
      
      // Update local state
      setDocument({ ...document, status: 'Obsolete' });
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to mark document as obsolete:', error);
      notify(`Failed to mark as obsolete: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setMarkingObsolete(false);
    }
  };

  function onBack(): void {
    navigate(-1);
  }

  function onEdit(id: string): void {
    navigate(`/documentsList/${id}/edit`);
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to List
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(document.status)}
              <h2 className="text-gray-900">{document.title}</h2>
            </div>
            <div className="text-gray-600 mb-2">{document.description}</div>
            <div className="flex items-center gap-4 text-gray-500">
              <span>Document Number: {document.documentNumber}</span>
              <span>•</span>
              <span>Current Version: v{document.currentVersion}</span>
              <span>•</span>
              <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                {document.status === 'Draft' && 'Draft'}
                {document.status === 'Review' && 'Under Review'}
                {document.status === 'Approved' && 'Approved'}
                {document.status === 'Obsolete' && 'Obsolete'}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            {document.status === 'Review' && (
              <>
                <button
                  onClick={() => {
                    setCommitAction('Approve');
                    setShowCommitModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  title="Approve this document"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => {
                    setCommitAction('Reject');
                    setShowCommitModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  title="Reject this document"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Reject
                </button>
              </>
            )}
            {document.status !== 'Obsolete' && (
              <button
                onClick={() => onEdit(document.id)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit Document
              </button>
            )}
            <button
              onClick={() => handleDownload(
                document.versions.find(v => v.isActive)?.fileUrl,
                document.currentVersion
              )}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Current Version
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-gray-500 mb-1">Category</div>
                <div className="text-gray-900">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    {document.category}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created Date</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.createdAt).toLocaleString('en-US')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {document.createdBy}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Last Updated</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.updatedAt).toLocaleString('en-US')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Updated By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {document.updatedBy}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {(document.tags || []).map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Version History */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <GitBranch className="w-5 h-5 text-gray-700" />
              <h3 className="text-gray-900 text-lg font-semibold">Version History</h3>
            </div>
            <div className="space-y-4">
              {(document.versions || []).map((version, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border transition-all ${
                    version.isActive
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-base font-semibold text-gray-900">
                        Version {version.version}
                      </span>
                      {version.isActive && (
                        <span className="px-2.5 py-0.5 bg-blue-600 text-white rounded-full text-xs font-medium">
                          Current
                        </span>
                      )}
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(version.status)}`}>
                        {version.status === 'Draft' && 'Draft'}
                        {version.status === 'Review' && 'Under Review'}
                        {version.status === 'Approved' && 'Approved'}
                        {version.status === 'Obsolete' && 'Obsolete'}
                      </span>
                    </div>
                    {version.fileUrl && (
                      <button
                        onClick={() => handleDownload(version.fileUrl, version.version)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                  </div>
                  {version.changes && (
                    <div className="mb-3 pb-3 border-b border-gray-200">
                      <div className="text-gray-700 text-sm leading-relaxed">{version.changes}</div>
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-gray-600 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(version.createdAt).toLocaleString('en-US')}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {version.createdBy}
                    </span>
                    {version.fileSize && (
                      <>
                        <span>•</span>
                        <span>{formatFileSize(version.fileSize)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval History */}
          {(document.approvalHistory || []).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 text-lg font-semibold mb-4">Approval History</h3>
              <div className="space-y-3">
                {(document.approvalHistory || []).map((record) => (
                  <div key={record.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{record.approver}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-600 text-sm">Level {record.level}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            record.action === 'Approved'
                              ? 'bg-green-100 text-green-700'
                              : record.action === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {record.action === 'Approved' && 'Approved'}
                          {record.action === 'Rejected' && 'Rejected'}
                          {record.action === 'Pending' && 'Pending'}
                        </span>
                      </div>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(record.timestamp).toLocaleString('en-US')}
                      </span>
                    </div>
                    {record.comments && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-gray-700 text-sm"><span className="font-medium">Comments:</span> {record.comments}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-gray-900 mb-3">Document Status</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Lifecycle</span>
                <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                  {document.status === 'Draft' && 'Draft'}
                  {document.status === 'Review' && 'Under Review'}
                  {document.status === 'Approved' && 'Approved'}
                  {document.status === 'Obsolete' && 'Obsolete'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Versions</span>
                <span className="text-gray-900">{(document.versions || []).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Approval Records</span>
                <span className="text-gray-900">{(document.approvalHistory || []).length}</span>
              </div>
            </div>
          </div>

          {/* Related Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-gray-900 mb-3">Related Actions</h4>
            <div className="space-y-2">
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                View Complete Audit Log
              </button>
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                Export Document Info
              </button>
              <button className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors">
                Print Document
              </button>
              {document.status === 'Approved' && (
                <button
                  onClick={() => setShowObsoleteModal(true)}
                  className="w-full px-3 py-2 text-left text-red-600 hover:bg-red-50 rounded transition-colors flex items-center gap-2"
                  title="Mark this approved document as obsolete"
                >
                  <Trash2 className="w-4 h-4" />
                  Mark as Obsolete
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mark as Obsolete Modal */}
      {showObsoleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Mark as Obsolete</h3>
            </div>
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium mb-1">Mark this document as obsolete?</p>
                  <p className="text-sm text-gray-600">Once marked as obsolete, this approved document will be archived and no longer available for new approvals. This action cannot be easily reversed.</p>
                </div>
              </div>
              {document && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-1 mb-4">
                  <div className="text-xs text-gray-600"><span className="font-medium">Document:</span> {document.documentNumber}</div>
                  <div className="text-xs text-gray-600"><span className="font-medium">Title:</span> {document.title}</div>
                  <div className="text-xs text-gray-600"><span className="font-medium">Current Status:</span> <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">Approved</span></div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowObsoleteModal(false)}
                disabled={markingObsolete}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMarkObsolete}
                disabled={markingObsolete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingObsolete ? 'Marking...' : 'Mark as Obsolete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commit Modal */}
      {showCommitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {commitAction === 'Approve' ? 'Approve Document' : 'Reject Document'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-900 mb-2">Document Details</div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><span className="font-medium">Number:</span> {document.documentNumber}</div>
                  <div><span className="font-medium">Title:</span> {document.title}</div>
                  <div><span className="font-medium">Version:</span> v{document.currentVersion}</div>
                  <div><span className="font-medium">Category:</span> {document.category}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {commitAction === 'Approve' ? 'Approval Comments (Optional)' : 'Rejection Reason (Optional)'}
                </label>
                <textarea
                  value={commitComments}
                  onChange={(e) => setCommitComments(e.target.value)}
                  placeholder="Enter your comments or reasons..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  disabled={committing}
                />
              </div>

              {commitAction === 'Reject' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-700">
                    <strong>Warning:</strong> Rejecting this document will send it back for revision.
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCommitModal(false);
                  setCommitComments('');
                }}
                disabled={committing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={committing}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  commitAction === 'Approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {committing ? 'Processing...' : (commitAction === 'Approve' ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
